import { Controller } from "@hotwired/stimulus";
import { get } from "@rails/request.js";
import { foldAccents, setFieldValue } from "../../shared/field_values";

export default class extends Controller {
  static targets = ["country", "state", "city"];
  static values = {
    selectCityText: { type: String, default: "Select a city" },
    errorLoadingText: { type: String, default: "Error loading cities" },
    initialCity: { type: String, default: "" },
    // Same country resolution chain as state-selector:
    // target → value → "MX" fallback. Eliminates the
    // need for a visible country `<select>`.
    country: { type: String, default: "MX" },
  };

  connect() {
    // Skip initialization on reconnects to avoid reloading
    // cities. The latch lives on the controller INSTANCE,
    // never on a data attribute: morphdom syncs attributes
    // from the incoming server HTML, which never carries
    // the flag, so a dataset latch is wiped by every morph
    // and connect() refetched the whole city list each
    // time (CAL-401). A preserved element keeps this
    // controller instance; a replaced one gets a fresh
    // instance and initializes again, which is exactly the
    // lifetime the latch is trying to describe.
    //
    // Two jobs here, with two different lifetimes, and
    // latching them together is what kept going wrong.
    //
    // The FETCH is what needs a latch: it is a network
    // round trip, and repeating it per reconnect is what
    // produced 36 city lookups for 5 address changes. It
    // is keyed on the pair the list depends on, so a morph
    // carrying a NEW state still refetches.
    //
    // The SELECTION needs no latch and must never be
    // skipped. A morph rewrites this <select> from server
    // HTML: the options already loaded survive, but the
    // selected value does not. Skipping it silently blanks
    // the customer's city, including when they switch
    // between two addresses in the SAME state, where the
    // fetch key is identical and nothing looks amiss.
    // Reapplying costs nothing and is idempotent
    // (CAL-401).
    if (!this.hasStateTarget || !this.hasCityTarget) return;
    if (!this.currentCountry() || !this.stateTarget.value) {
      return;
    }

    const key = this.listKey();

    if (this.loadedFor === key) {
      this.selectInitialCity();
      return;
    }

    this.loadedFor = key;
    this.updateCities();
  }

  // The pair the city list itself depends on. Deliberately
  // NOT including the chosen city: that changes between
  // two addresses in the same state, and it selects an
  // option rather than deciding which list to load.
  listKey() {
    return [
      this.currentCountry(),
      this.hasStateTarget ? this.stateTarget.value : "",
    ].join("/");
  }

  currentCountry() {
    if (this.hasCountryTarget && this.countryTarget.value) {
      return this.countryTarget.value;
    }
    return this.countryValue;
  }

  addressSelected(event) {
    const { address } = event.detail;
    if (!address.city) return Promise.resolve();

    // Hand the city to `updateCities()` and let IT do the
    // selecting. It resolves the value against the list it
    // just loaded (exact, then accent-folded, then
    // word-boundary prefix), which is the only way a saved
    // "Benito Juárez" finds an option the API spells
    // "Benito Juarez".
    //
    // Assigning `address.city` onto the target here
    // instead (what this did before) sets a value no
    // option carries, so the select silently falls back to
    // blank and the customer submits no city at all. That
    // bug was invisible for as long as this controller
    // fired three overlapping requests per load: a later
    // one re-ran the matcher after the clobber and put the
    // right value back. Collapsing those duplicates
    // removed the cover, not the bug (CAL-401).
    this.initialCityValue = address.city;
    // A pick names its city outright, so the list it
    // loads resolves it even when the list is the one
    // already on screen. See `reselectOrInitialize`.
    this.pickPending = true;

    return this.updateCities();
  }

  updateCities() {
    const country = this.currentCountry();
    const state = this.stateTarget.value;

    if (!country || !state) {
      this.cityTarget.innerHTML = `<option value="">${this.selectCityTextValue}</option>`;
      this.cityTarget.disabled = true;
      return Promise.resolve();
    }

    // Enable city select when state is selected
    this.cityTarget.disabled = false;

    // Three separate paths ask for the same list on a
    // single page load (this controller's own connect(),
    // the `change` the state selector fires once its own
    // list is populated, and the `addressSelected`
    // broadcast), so one answer cost three round trips
    // (CAL-401). Sharing the in-flight PROMISE is what
    // collapses them: the later callers arrive while the
    // first request is still open, and each still gets a
    // promise that resolves once the options are in the
    // DOM, so their `.then()` chains keep working.
    const requestKey = `${country}/${state}`;

    if (
      this.citiesRequest &&
      this.citiesRequestKey === requestKey
    ) {
      return this.citiesRequest;
    }

    this.citiesRequestKey = requestKey;

    // Use @rails/request.js to fetch cities
    this.citiesRequest = get(
      `/shared/api/cities?country=${country}&state=${state}`
    )
      .then((response) => {
        if (response.ok) {
          return response.json.then((cities) => {
            // Read before the rewrite, which blanks the
            // field. See `reselectOrInitialize`.
            const relisting = this.listedFor === requestKey;
            const current = this.cityTarget.value;

            this.cityTarget.innerHTML = `<option value="">${this.selectCityTextValue}</option>`;
            if (Array.isArray(cities)) {
              cities.forEach((city) => {
                const option = document.createElement("option");
                option.value = city;
                option.textContent = city;
                this.cityTarget.appendChild(option);
              });

              this.listedFor = requestKey;
              this.loadedFor = requestKey;
              this.reselectOrInitialize(relisting, current);
            }
          });
        } else {
          this.cityTarget.innerHTML = `<option value="">${this.errorLoadingTextValue}</option>`;
          this.cityTarget.disabled = true;
        }
      })
      .catch((error) => {
        if (this.hasCityTarget) {
          this.cityTarget.innerHTML = `<option value="">${this.errorLoadingTextValue}</option>`;
          this.cityTarget.disabled = true;
        }
      })
      .finally(() => {
        // Only IN-FLIGHT requests are shared, never a
        // settled one. The options this request wrote can
        // be overwritten by a different state, so a later
        // ask for the same key has to actually re-run and
        // repopulate: a cached settled promise would
        // resolve instantly against whatever another
        // state left in the DOM. Guarded on the key so a
        // newer request already in flight is not cleared.
        if (this.citiesRequestKey === requestKey) {
          this.citiesRequest = null;
        }
      });

    return this.citiesRequest;
  }

  // A list fetched again for the state it already
  // holds changes no answer, so the city the customer
  // has stays. A list for a DIFFERENT state, the first
  // one, and any list a pick asked for resolve
  // `initialCityValue` instead: a pick is a new answer.
  //
  // The second fetch is not hypothetical. A discount
  // code applied on the delivery checkout morphs the
  // page (CAL-522): the customer's city survives it, and
  // `state-selector` then reconnects, re-selects the
  // state and fires `change`, which lands here. The
  // morph has also put `initialCityValue` back to the
  // server's, which for a guest is blank, so resolving
  // it emptied the city the morph had just kept, and
  // the address with it.
  //
  // `loadedFor` is written here as well as in
  // `connect()`: a list loaded by a pick rather than on
  // connect is loaded all the same, and a reconnect that
  // did not know it refetched it and landed here.
  reselectOrInitialize(relisting, current) {
    const picked = this.pickPending;
    this.pickPending = false;

    if (relisting && current && !picked) {
      setFieldValue(this.cityTarget, current);
      return;
    }

    this.selectInitialCity();
  }

  // Resolve `initialCityValue` against the options
  // currently in the <select>. Direct match first; failing
  // that an accent-folded match, then a word-boundary
  // prefix match, so values from Google Places or a saved
  // address ("Cuauhtémoc", or the longer locality
  // "Naucalpan de Juárez") land on our ASCII list
  // ("Cuauhtemoc", "Naucalpan").
  //
  // And where NOTHING in the list is this city, the city
  // still wins: `setFieldValue` adds it rather than
  // leaving the field empty. `CS.cities` is a convenience
  // list, not a register of what a place may be called:
  // for CMX it holds the alcaldías and "Mexico City" and
  // has no "Ciudad de México", which is what Google
  // answers with for a CDMX address. Dropping it left a
  // required <select> blank under a filled-in address,
  // and `courier-quote` reads a missing city as a missing
  // ADDRESS: the checkout asked for one it already had
  // and never requested another quote (CAL-464).
  //
  // Idempotent and free: no network, and running it
  // against an already-correct <select> is a no-op. That
  // is what lets `connect()` reapply it on every reconnect
  // without re-fetching.
  selectInitialCity() {
    if (!this.hasCityTarget) return;
    if (!this.initialCityValue) return;

    const target = this.initialCityValue;
    const before = this.cityTarget.value;
    const options = Array.from(this.cityTarget.options);
    const direct = options.find((o) => o.value === target);
    const folded = foldAccents(target);
    const exact = options.find(
      (o) => foldAccents(o.value) === folded
    );
    const match =
      direct ||
      exact ||
      this.prefixMatch(folded, options);

    setFieldValue(
      this.cityTarget,
      match ? match.value : target
    );
    this.announceCity(before);
  }

  /**
   * Tell the page the city landed.
   *
   * This select is populated over the network, so the
   * city arrives one round trip AFTER the pick that
   * asked for it. Everything downstream (the courier
   * quote above all) reads the DOM, and assigning
   * `.value` in script fires no event, so nothing ever
   * learned that the field had stopped being empty.
   *
   * The consequence was a checkout frozen in its own
   * empty state: `collectAddress()` requires a city,
   * found none at the moment of the pick, returned
   * null, and the panel settled on "Add your address
   * to see delivery time & fee" over a filled-in
   * address row. No retry followed, because nothing had
   * happened. A customer whose address was genuinely
   * out of range never even got told that: the request
   * that would have said so was never sent (CAL-442).
   *
   * Only on a real transition, so a reconnect that
   * re-applies the same value stays the no-op
   * `selectInitialCity` promises to be.
   */
  announceCity(previousValue) {
    if (this.cityTarget.value === previousValue) return;

    this.cityTarget.dispatchEvent(
      new Event("change", { bubbles: true })
    );
  }

  // Word-boundary prefix match against an already-folded
  // target, in both directions: an option that is a prefix
  // of the target ("Naucalpan" for "naucalpan de juarez")
  // or, failing that, an option the target is a prefix of
  // ("san luis" -> "San Luis Potosi"). The shorter string
  // must end at a space in the longer one, so "san luis"
  // never matches "san luisito". Within the winning
  // direction the longest option wins; a tie is ambiguous
  // so we return null rather than guess.
  prefixMatch(folded, options) {
    const boundary = (a, b) => a === b || a.startsWith(b + " ");
    const forward = [];
    const reverse = [];
    options.forEach((o) => {
      if (!o.value) return;
      const f = foldAccents(o.value);
      if (boundary(folded, f)) forward.push({ o, len: f.length });
      else if (boundary(f, folded)) reverse.push({ o, len: f.length });
    });
    const pool = forward.length ? forward : reverse;
    if (!pool.length) return null;
    const max = Math.max(...pool.map((c) => c.len));
    const winners = pool.filter((c) => c.len === max);
    return winners.length === 1 ? winners[0].o : null;
  }
}
